/**
 * Mini-POS embebido en panel de llamada activa (Voice Commerce M3).
 *
 * El agente busca productos del catálogo, los agrega a un cart en construcción,
 * y con 1 click genera order + MP payment link + envía por WhatsApp al cliente.
 *
 * Uso:
 *   <AgentQuickOrderPanel
 *     tenantId={tenantId}
 *     customerPhone={remotePhone}
 *     customerName={leadName}
 *     crmCallId={crmCallId}
 *   />
 *
 * Plan: docs/plan/PLAN-VOICE-COMMERCE-M3-AGENT-QUICK-ORDER.md
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Spinner,
  Text,
  TextField,
} from '@/lib/polaris';
import { useAdminToast } from '@/contexts/AdminToastContext';
import {
  buildQuickOrderItemFromProduct,
  calculateQuickOrderTotals,
  mergeOrAppendItem,
  useAgentQuickOrder,
  useProductSearch,
  type QuickOrderItem,
  type QuickProduct,
} from '@/hooks/useAgentQuickOrder';

interface AgentQuickOrderPanelProps {
  tenantId: string;
  customerPhone: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  crmCallId?: string | null;
  /** Compactar para variant 'fab' (FAB del dialer flotante) */
  compact?: boolean;
}

const DEBOUNCE_MS = 300;

export function AgentQuickOrderPanel({
  tenantId,
  customerPhone,
  customerName,
  customerEmail,
  crmCallId,
  compact = false,
}: AgentQuickOrderPanelProps) {
  const toast = useAdminToast();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [items, setItems] = useState<QuickOrderItem[]>([]);
  const [lastSuccess, setLastSuccess] = useState<{ orderId: string; link?: string; waSent: boolean } | null>(null);

  // Debounce manual (avoid extra dep)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const search = useProductSearch(debouncedSearch, { tenantId });
  const mutation = useAgentQuickOrder();

  const totals = useMemo(() => calculateQuickOrderTotals(items), [items]);

  const phoneIsValid = !!customerPhone && /^\+[1-9][0-9]{6,14}$/.test(customerPhone);
  const canSubmit = items.length > 0 && phoneIsValid && !mutation.isPending;

  const handleAddProduct = (product: QuickProduct) => {
    setItems((prev) => mergeOrAppendItem(prev, buildQuickOrderItemFromProduct(product)));
    // Limpiar search después de agregar (UX)
    setSearchInput('');
    setDebouncedSearch('');
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleQuantityChange = (index: number, delta: number) => {
    setItems((prev) => {
      const copy = [...prev];
      const next = (copy[index].quantity ?? 1) + delta;
      if (next <= 0) return prev.filter((_, i) => i !== index);
      copy[index] = { ...copy[index], quantity: next };
      return copy;
    });
  };

  const handleSubmit = async () => {
    if (!phoneIsValid || items.length === 0) return;
    try {
      const res = await mutation.mutateAsync({
        customer_phone: customerPhone!,
        customer_name: customerName ?? undefined,
        customer_email: customerEmail ?? undefined,
        items,
        crm_call_id: crmCallId ?? undefined,
      });
      if (res.ok) {
        setLastSuccess({
          orderId: res.order_id ?? '',
          link: res.payment_link,
          waSent: res.wa_sent,
        });
        setItems([]);
        setSearchInput('');
        setDebouncedSearch('');
        toast.success(
          res.wa_sent
            ? `Pedido creado y link enviado por WhatsApp`
            : `Pedido creado. Copia el link manualmente abajo.`,
        );
      } else {
        toast.error(`Error: ${(res.errors ?? ['unknown']).join(', ')}`);
      }
    } catch (e) {
      toast.error(`Error: ${(e as Error).message}`);
    }
  };

  return (
    <Card>
      <Box padding={compact ? '300' : '400'}>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h3" variant="headingMd">Crear pedido rápido</Text>
            {items.length > 0 && (
              <Badge tone="info">{`${totals.itemCount} item${totals.itemCount !== 1 ? 's' : ''}`}</Badge>
            )}
          </InlineStack>

          {!phoneIsValid && (
            <Banner tone="warning">
              <Text as="p">Sin número en E.164 no se puede enviar el link por WhatsApp.</Text>
            </Banner>
          )}

          {/* Search box */}
          <TextField
            label="Buscar producto"
            labelHidden
            placeholder="Buscar producto por nombre o categoría…"
            value={searchInput}
            onChange={setSearchInput}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => {
              setSearchInput('');
              setDebouncedSearch('');
            }}
          />

          {/* Search results */}
          {search.isFetching && debouncedSearch.length >= 2 && (
            <InlineStack align="center"><Spinner accessibilityLabel="Buscando" size="small" /></InlineStack>
          )}

          {!search.isFetching && (search.data ?? []).length > 0 && (
            <BlockStack gap="100">
              {(search.data ?? []).slice(0, 6).map((p) => (
                <Box
                  key={p.id}
                  borderWidth="025"
                  borderColor="border"
                  borderRadius="200"
                  padding="200"
                >
                  <InlineStack align="space-between" blockAlign="center" gap="200">
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" fontWeight="semibold">{p.name}</Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {p.category} · ${Number(p.price).toFixed(2)}
                        {p.has_variants ? ' · con variantes' : ''}
                      </Text>
                    </BlockStack>
                    <Button size="micro" variant="primary" onClick={() => handleAddProduct(p)}>
                      Agregar
                    </Button>
                  </InlineStack>
                </Box>
              ))}
            </BlockStack>
          )}

          {!search.isFetching && debouncedSearch.length >= 2 && (search.data ?? []).length === 0 && (
            <Text as="p" tone="subdued" variant="bodySm">Sin resultados para "{debouncedSearch}"</Text>
          )}

          {/* Cart en construcción */}
          {items.length > 0 && (
            <BlockStack gap="200">
              <Text as="h4" variant="headingSm">Tu pedido</Text>
              {items.map((it, idx) => (
                <Box
                  key={`${it.product_id ?? 'manual'}-${idx}`}
                  borderWidth="025"
                  borderColor="border"
                  borderRadius="200"
                  padding="200"
                  background="bg-surface-secondary"
                >
                  <InlineStack align="space-between" blockAlign="center" gap="200">
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" fontWeight="semibold">{it.product_name}</Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        ${it.unit_price.toFixed(2)} c/u
                      </Text>
                    </BlockStack>
                    <InlineStack gap="100" blockAlign="center">
                      <Button size="micro" onClick={() => handleQuantityChange(idx, -1)}>−</Button>
                      <Text as="span" variant="bodySm" fontWeight="semibold">{it.quantity}</Text>
                      <Button size="micro" onClick={() => handleQuantityChange(idx, 1)}>+</Button>
                      <Button size="micro" variant="tertiary" tone="critical" onClick={() => handleRemoveItem(idx)}>
                        Quitar
                      </Button>
                    </InlineStack>
                  </InlineStack>
                </Box>
              ))}

              <InlineStack align="space-between">
                <Text as="span" variant="bodyMd" fontWeight="semibold">Total</Text>
                <Text as="span" variant="headingMd">${totals.subtotal.toFixed(2)}</Text>
              </InlineStack>

              <Button
                variant="primary"
                size="large"
                disabled={!canSubmit}
                loading={mutation.isPending}
                onClick={handleSubmit}
                fullWidth
              >
                {phoneIsValid
                  ? 'Cobrar y enviar link por WhatsApp'
                  : 'Sin teléfono — no se puede cobrar'}
              </Button>
            </BlockStack>
          )}

          {/* Last success: muestra link copiable si WA falló */}
          {lastSuccess && (
            <Banner tone={lastSuccess.waSent ? 'success' : 'warning'} title={lastSuccess.waSent ? 'Link enviado por WhatsApp' : 'Order creada (WA no enviado)'}>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm">
                  Order ID: <code>{lastSuccess.orderId}</code>
                </Text>
                {lastSuccess.link && (
                  <Box>
                    <Text as="p" variant="bodySm">Link de pago (copiable):</Text>
                    <Text as="span" variant="bodySm" breakWord>
                      <a href={lastSuccess.link} target="_blank" rel="noreferrer">{lastSuccess.link}</a>
                    </Text>
                  </Box>
                )}
              </BlockStack>
            </Banner>
          )}
        </BlockStack>
      </Box>
    </Card>
  );
}
